import React, { useState } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { uuid, canAddCrossSell, canRemoveCrossSell, mapCrossSellOpportunity } from '../../core/helpers';
import { sb } from '../../core/db';
import { Icon, InlineIcon } from '../../components/Icon';

const STAFF = new Set(["admin", "manager", "employee", "sales"]);

function CrossSellAlert({ client, tasks }) {
  const { setView } = useApp();
  const [dismissed, setDismissed] = useState(false);
  const clientTasks = tasks.filter(t => t.clientId === client.id && !t.isRecurring);
  const allDone = clientTasks.length > 0 && clientTasks.every(t => t.status === "completed");
  if (!allDone || dismissed) return null;

  return (
    <div className="cross-sell-alert" style={{ padding:"14px 18px",background:"linear-gradient(135deg,var(--navy),#1E3A5F)",borderRadius:12,marginBottom:16,color:"#fff",display:"flex",alignItems:"center",gap:14 }}>
      <Icon name="target" size={28} />
      <div style={{ flex:1 }}>
        <div style={{ fontSize:14,fontWeight:700,marginBottom:3 }}>Registration Complete — Cross-sell Opportunity!</div>
        <div style={{ fontSize:12,opacity:.8 }}><strong style={{ color:"#C9A14A" }}>{client.name}</strong> has completed all registration tasks.</div>
      </div>
      <div className="cross-sell-alert-actions" style={{ display:"flex",gap:8,flexShrink:0 }}>
        <button type="button" className="btn btn-gold btn-sm" onClick={() => setView("cross-sell")}>View Opportunities →</button>
        <button type="button" onClick={() => setDismissed(true)} style={{ background:"rgba(255,255,255,.1)",border:"none",color:"rgba(255,255,255,.5)",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:11 }}>Dismiss</button>
      </div>
    </div>
  );
}

function CrossSellPage() {
  const {
    user, clients, tasks, crossSellOpps, setCrossSellOpps,
    visibleCompanyIds, myCompanies, showToast, setView, runSubmitting, submitBusy,
  } = useApp();
  const isStaff = STAFF.has(user?.role);
  const isClient = user?.role === "client";

  const clientOptions = isClient
    ? myCompanies
    : clients;

  const [form, setForm] = useState({
    clientId: isClient ? (visibleCompanyIds?.[0] || clientOptions[0]?.id || "") : "",
    serviceName: "",
    notes: "",
  });
  const [showAdd, setShowAdd] = useState(false);

  const autoOpportunities = isStaff ? clients.filter(c => {
    const clientTasks = tasks.filter(t => t.clientId === c.id && !t.isRecurring);
    if (clientTasks.length === 0) return false;
    return clientTasks.every(t => t.status === "completed");
  }) : [];

  const addOpportunity = async (payload) => {
    const cl = clientOptions.find(c => c.id === payload.clientId) || clients.find(c => c.id === payload.clientId);
    const row = {
      id: uuid(),
      company_id: payload.clientId,
      client_name: cl?.name || user?.name || "",
      service_name: payload.serviceName,
      notes: payload.notes || "",
      created_by_name: user?.name || "",
    };
    const res = await sb.insert("cross_sell_opportunities", row);
    if (!res.length) {
      showToast("Could not save opportunity", "error");
      return false;
    }
    setCrossSellOpps(opps => [...opps, mapCrossSellOpportunity(res[0] || row)]);
    showToast("Opportunity added", "success");
    return true;
  };

  const addWithOverlay = async (payload, label = 'Submitting to sales…') => {
    if (runSubmitting) {
      return runSubmitting(label, () => addOpportunity(payload), 'marketplace');
    }
    return addOpportunity(payload);
  };

  const removeOpportunity = async (opp) => {
    if (!canRemoveCrossSell(user, opp)) {
      showToast("You can only remove opportunities you added", "error");
      return;
    }
    await sb.delete("cross_sell_opportunities", `id=eq.${opp.id}`);
    setCrossSellOpps(opps => opps.filter(o => o.id !== opp.id));
    showToast("Removed", "success");
  };

  const submitForm = async () => {
    if (!canAddCrossSell(user)) {
      showToast("Not allowed to add opportunities", "error");
      return;
    }
    if (!form.clientId || !form.serviceName.trim()) {
      showToast("Select client and enter a service name", "error");
      return;
    }
    const ok = await addWithOverlay(form);
    if (ok) {
      setForm(f => ({ ...f, serviceName: "", notes: "" }));
      setShowAdd(false);
    }
  };

  const promoteAuto = async (client, serviceName) => {
    await addWithOverlay({ clientId: client.id, serviceName, notes: "Auto-suggested after registration complete" });
  };

  return (
    <>
      <div style={{ marginBottom: 20, padding: "14px 18px", background: "linear-gradient(135deg,var(--navy),#1E3A5F)", borderRadius: 10, color: "#fff", display: "flex", alignItems: "center", gap: 14 }}>
        <Icon name="target" size={28} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{isClient ? "Grow My Business" : "Cross-Sell Opportunities"}</div>
          <div style={{ fontSize: 12, opacity: .7, marginTop: 2 }}>
            {isClient
              ? "Add services you're interested in. You can remove only entries you added yourself."
              : "Track and manage upsell opportunities across all clients."}
          </div>
        </div>
      </div>

      {canAddCrossSell(user) && (
        <div style={{ marginBottom: 20 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            <InlineIcon name="add" size={14} gap={5}>Add opportunity</InlineIcon>
          </button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-head">
          <div className="card-title">Saved opportunities</div>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>{crossSellOpps.length} item{crossSellOpps.length !== 1 ? "s" : ""}</div>
        </div>
        {crossSellOpps.length === 0 ? (
          <div className="empty"><div className="empty-icon"><Icon name="target" size={32} /></div><div>No opportunities yet — add one above</div></div>
        ) : (
          crossSellOpps.map(opp => (
            <div key={opp.id} className="row-item cross-sell-row">
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--gold-lt)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="target" size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>{opp.serviceName}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                  {opp.clientName || "Client"} · Added by {opp.createdByName || (opp.createdBy === user?.id ? "you" : "team")}
                  {opp.notes ? ` · ${opp.notes}` : ""}
                </div>
              </div>
              <div className="cross-sell-row-actions" style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setView("marketplace")}>Pitch →</button>
                {canRemoveCrossSell(user, opp) && (
                  <button type="button" className="btn btn-sm" style={{ background: "#FEF2F2", color: "var(--red)", border: "1px solid #FECACA" }} onClick={() => removeOpportunity(opp)}>Remove</button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {isStaff && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-head">
              <div className="card-title"><InlineIcon name="check" size={16} gap={6}>Registration complete — suggested pitches</InlineIcon></div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{autoOpportunities.length} client{autoOpportunities.length !== 1 ? "s" : ""}</div>
            </div>
            {autoOpportunities.length === 0 ? (
              <div className="empty"><div>No auto-detected opportunities right now</div></div>
            ) : (
              autoOpportunities.map(c => (
                <div key={c.id} className="row-item">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{c.type} · Registered {c.createdAt}</div>
                  </div>
                  <button type="button" className="btn btn-sm" onClick={() => promoteAuto(c, "Annual compliance package")}>+ Add to list</button>
                </div>
              ))
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title"><InlineIcon name="warning" size={16} gap={6}>Missing annual compliance</InlineIcon></div>
            </div>
            <div className="tbl-wrap">
              <table>
                <thead><tr><th>Client</th><th>Missing</th><th>Action</th></tr></thead>
                <tbody>
                  {clients.filter(c => {
                    const ct = tasks.filter(t => t.clientId === c.id);
                    return !ct.some(t => t.category === "GST") || !ct.some(t => t.category === "ROC");
                  }).slice(0, 10).map(c => {
                    const ct = tasks.filter(t => t.clientId === c.id);
                    const missing = [!ct.some(t => t.category === "GST") && "GST Filing", !ct.some(t => t.category === "ROC") && "Annual ROC"].filter(Boolean);
                    return (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 600 }}>{c.name}</td>
                        <td>{missing.map(m => <span key={m} style={{ fontSize: 11, background: "#FEF2F2", color: "var(--red)", padding: "2px 8px", borderRadius: 4, marginRight: 4 }}>{m}</span>)}</td>
                        <td>
                          <button type="button" className="btn btn-sm" onClick={() => promoteAuto(c, missing[0] || "Compliance services")}>+ Add</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showAdd && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{isClient ? "Add service interest" : "Add cross-sell opportunity"}</div>
              <button type="button" className="modal-close" onClick={() => setShowAdd(false)}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                {!isClient && (
                  <div className="f-group">
                    <label className="f-label">Client <span className="f-req">*</span></label>
                    <select className="f-select" value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
                      <option value="">Select client…</option>
                      {clientOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="f-group">
                  <label className="f-label">Service <span className="f-req">*</span></label>
                  <input className="f-input" placeholder="e.g. GST Filing" value={form.serviceName} onChange={e => setForm(f => ({ ...f, serviceName: e.target.value }))} />
                </div>
                <div className="f-group">
                  <label className="f-label">Notes</label>
                  <textarea className="f-textarea" placeholder="Optional notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} />
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={submitForm} disabled={submitBusy}>
                {submitBusy ? (
                  <span className="btn-spinner-wrap">
                    <span className="btn-spinner" aria-hidden="true" />
                    Saving…
                  </span>
                ) : 'Add opportunity'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { CrossSellAlert, CrossSellPage };
